import {
  ACCOUNT_COLUMNS,
  accountCellValue,
  CONTACT_COLUMNS,
  contactCellValue,
  PAYMENT_TERM_COLUMNS,
  paymentTermCellValue,
  RecordLookup,
  SHIP_TO_COLUMNS,
  shipToCellValue,
  toAccountOption,
  toContactOption,
  toPaymentTermOption,
  toShipToOption,
  useLookupSearch,
} from '@components';
import { Button } from '@salesforce/design-system-react';
import { useAccountStore } from '@store';
import { IconCategory, type ILookupOption } from '@types';
import { useCallback, useState } from 'react';

import { useViewSdk } from '../../ViewSdkContext';

const orderId = '801WE00001GM3K5YAL';

export const HomePage: React.FC = () => {
  const viewSdk = useViewSdk();
  const {
    searchAccounts,
    searchAccountsModal,
    searchContacts,
    searchContactsModal,
    searchPaymentTerms,
    searchPaymentTermsModal,
    searchShipTos,
    searchShipTosModal,
  } = useLookupSearch();
  const { accountId, accountName } = useAccountStore();
  const [nonContractedAccount, setNonContractedAccount] = useState<ILookupOption>();
  const [accountContact, setAccountContact] = useState<ILookupOption>();
  const [shipTo, setShipTo] = useState<ILookupOption>();
  const [paymentTerm, setPaymentTerm] = useState<ILookupOption>();
  const searchScopedContacts = useCallback(
    (filters: Parameters<typeof searchContactsModal>[0], limit: number) => {
      if (!accountId) throw new Error('An Account is required to search Contacts');
      return searchContactsModal(filters, limit, accountId);
    },
    [accountId, searchContactsModal],
  );
  const openOrder = (eventType: 'open-order' | 'open-order-subtab') => {
    viewSdk.dispatchEvent?.(new CustomEvent(eventType, { detail: { recordId: orderId } }));
  };

  return (
    <section className="slds-p-around_medium">
      <h1 className="slds-text-heading_large slds-m-bottom_medium">Complex Contracts</h1>
      {accountId ? <p className="slds-m-bottom_medium">Preselected Account: {accountName ?? accountId}</p> : null}
      <div aria-label="Open Order" className="slds-button-group" role="group">
        <Button
          label="Open Order in New Tab"
          onClick={() => {
            openOrder('open-order');
          }}
          variant="brand"
        />
        <Button
          label="Open Order as Subtab"
          onClick={() => {
            openOrder('open-order-subtab');
          }}
          variant="neutral"
        />
      </div>
      <div className="slds-grid slds-wrap slds-gutters slds-m-top_large">
        <div className="slds-col slds-size_1-of-1 slds-medium-size_1-of-2">
          <RecordLookup
            iconCategory={IconCategory.Standard}
            iconName="account"
            label="Non-Contracted Account"
            modal={{
              columns: ACCOUNT_COLUMNS,
              getCellValue: accountCellValue,
              search: searchAccountsModal,
              toOption: toAccountOption,
            }}
            onChange={setNonContractedAccount}
            placeholder="Search Accounts…"
            search={searchAccounts}
            value={nonContractedAccount}
          />
        </div>
        <div className="slds-col slds-size_1-of-1 slds-medium-size_1-of-2">
          <RecordLookup
            iconCategory={IconCategory.Standard}
            iconName="address"
            label="Ship To"
            modal={{
              columns: SHIP_TO_COLUMNS,
              getCellValue: shipToCellValue,
              search: searchShipTosModal,
              toOption: toShipToOption,
            }}
            onChange={setShipTo}
            placeholder="Search Ship To…"
            search={searchShipTos}
            value={shipTo}
          />
        </div>
        <div className="slds-col slds-size_1-of-1 slds-medium-size_1-of-2">
          <RecordLookup
            iconCategory={IconCategory.Standard}
            iconName="currency"
            label="Payment Term"
            modal={{
              columns: PAYMENT_TERM_COLUMNS,
              getCellValue: paymentTermCellValue,
              search: searchPaymentTermsModal,
              toOption: toPaymentTermOption,
            }}
            onChange={setPaymentTerm}
            placeholder="Search Payment Terms…"
            search={searchPaymentTerms}
            value={paymentTerm}
          />
        </div>
        <div className="slds-col slds-size_1-of-1 slds-medium-size_1-of-2">
          <RecordLookup
            disabled={!accountId}
            iconCategory={IconCategory.Standard}
            iconName="contact"
            label="Account Contact"
            modal={{
              columns: CONTACT_COLUMNS,
              getCellValue: contactCellValue,
              search: searchScopedContacts,
              toOption: toContactOption,
            }}
            onChange={setAccountContact}
            placeholder={accountId ? 'Search Contacts…' : 'No preselected Account to search Contacts for'}
            search={term => {
              // Driven off the preselected Account (page context), not the Non-Contracted Account lookup above.
              if (!accountId) {
                console.debug('[HomePage] searchContacts skipped — no preselected Account');
                return Promise.resolve([]);
              }
              return searchContacts(term, accountId);
            }}
            value={accountContact}
          />
        </div>
      </div>
    </section>
  );
};
